import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { supabase } from '@/lib/supabase'
import { getTeacherCohorts, getTeacherWorkshops } from '@/lib/db'
import type { Cohort, UnitWorkshop, ProfessorNote } from '@/types'
import {
  Video,
  Users,
  UsersRound,
  Calendar,
  Clock,
  Star,
  TrendingUp,
  ChevronRight,
  FolderOpen,
  Loader2,
  Eye,
  StickyNote,
  CheckCircle2,
  Circle
} from 'lucide-react'

export default function TeacherDashboard() {
  const { profile, teacher } = useAuth()

  const [cohorts, setCohorts] = useState<Cohort[]>([])
  const [workshops, setWorkshops] = useState<UnitWorkshop[]>([])
  const [notes, setNotes] = useState<ProfessorNote[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!profile?.id || !teacher?.id) return

    async function loadData() {
      setLoading(true)
      const [cohortsRes, workshopsRes, notesRes] = await Promise.all([
        getTeacherCohorts(profile!.id),
        getTeacherWorkshops(teacher!.id),
        (supabase.from('professor_notes') as any)
          .select('*')
          .eq('teacher_id', profile!.id)
          .order('is_completed', { ascending: true })
          .order('created_at', { ascending: false })
      ])

      if (cohortsRes.data) setCohorts(cohortsRes.data)
      if (workshopsRes.data) setWorkshops(workshopsRes.data)
      if (notesRes.data) setNotes(notesRes.data)
      setLoading(false)
    }

    loadData()
  }, [profile?.id, teacher?.id])

  // Calcular stats reales
  const totalStudents = [...new Set(cohorts.flatMap(c => c.student_ids || []))].length
  const today = new Date().toISOString().split('T')[0]
  const upcomingWorkshops = workshops.filter(w => w.scheduled_date && w.scheduled_date >= today)
  const totalWorkshops = workshops.length
  const rating = teacher?.rating || 0
  const totalReviews = teacher?.total_reviews || 0

  // Últimos 3 talleres próximos
  const nextWorkshops = upcomingWorkshops.slice(0, 3)

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header de bienvenida */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-6 text-white">
        <h1 className="text-2xl font-bold mb-2">
          Hola, {profile?.first_name}!
        </h1>
        <p className="opacity-90">
          Bienvenido a tu panel de profesor
        </p>

        {rating > 0 && (
          <div className="mt-4 flex items-center gap-2">
            <Star className="w-5 h-5 text-yellow-300 fill-yellow-300" />
            <span className="text-lg font-semibold">{rating.toFixed(1)}</span>
            <span className="opacity-75">({totalReviews} reseñas)</span>
          </div>
        )}
      </div>

      {/* Stats rápidos */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-card rounded-xl p-4 border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{totalStudents}</p>
              <p className="text-xs text-muted-foreground">
                Estudiantes
              </p>
            </div>
          </div>
        </div>

        <div className="bg-card rounded-xl p-4 border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Video className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{totalWorkshops}</p>
              <p className="text-xs text-muted-foreground">
                Talleres
              </p>
            </div>
          </div>
        </div>

        <div className="bg-card rounded-xl p-4 border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <Calendar className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{upcomingWorkshops.length}</p>
              <p className="text-xs text-muted-foreground">
                Próximos
              </p>
            </div>
          </div>
        </div>

        <div className="bg-card rounded-xl p-4 border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <UsersRound className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{cohorts.length}</p>
              <p className="text-xs text-muted-foreground">
                Cohortes
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Próximos talleres */}
        <div className="bg-card rounded-xl border overflow-hidden">
          <div className="p-4 border-b flex items-center justify-between">
            <h2 className="font-semibold flex items-center gap-2">
              <Video className="w-5 h-5 text-primary" />
              Próximos Talleres
            </h2>
            <Link to="/teacher/workshops" className="text-sm text-primary hover:underline">
              Ver todos
            </Link>
          </div>
          <div className="divide-y">
            {nextWorkshops.length === 0 ? (
              <div className="p-6 text-center text-muted-foreground">
                No hay talleres próximos
              </div>
            ) : (
              nextWorkshops.map((workshop) => (
                <Link
                  key={workshop.id}
                  to={`/teacher/workshop/${workshop.id}/attendance`}
                  className="flex items-center gap-4 p-4 hover:bg-muted/50 transition-colors"
                >
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Video className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">
                      {workshop.title_es || workshop.title}
                    </p>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Clock className="w-3 h-3" />
                      <span>{workshop.scheduled_date?.slice(0, 10)} - {workshop.scheduled_date?.slice(11, 16) || '---'}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-sm font-medium">
                      {(workshop as any).attendance?.length || 0}/{workshop.max_participants || '∞'}
                    </span>
                    <p className="text-xs text-muted-foreground">
                      inscritos
                    </p>
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>

        {/* Cohortes activas */}
        <div className="bg-card rounded-xl border overflow-hidden">
          <div className="p-4 border-b flex items-center justify-between">
            <h2 className="font-semibold flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" />
              Mis Cohortes
            </h2>
            <Link to="/teacher/cohorts" className="text-sm text-primary hover:underline">
              Ver todas
            </Link>
          </div>
          <div className="divide-y">
            {cohorts.length === 0 ? (
              <div className="p-6 text-center text-muted-foreground">
                No tienes cohortes asignadas
              </div>
            ) : (
              cohorts.slice(0, 3).map((cohort) => (
                <Link
                  key={cohort.id}
                  to={`/teacher/cohorts/${cohort.id}`}
                  className="flex items-center gap-4 p-4 hover:bg-muted/50 transition-colors"
                >
                  <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                    <UsersRound className="w-5 h-5 text-orange-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{cohort.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {cohort.level?.name || 'Sin nivel'} · {(cohort.student_ids || []).length} alumnos
                    </p>
                  </div>
                  <div className="text-right text-sm text-muted-foreground">
                    U{cohort.unlocked_unit_index + 1}
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Mis Notas */}
      {notes.length > 0 && (
        <div className="bg-card rounded-xl border overflow-hidden">
          <div className="p-4 border-b flex items-center justify-between">
            <h2 className="font-semibold flex items-center gap-2">
              <StickyNote className="w-5 h-5 text-amber-500" />
              Mis Notas
              <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                {notes.filter(n => !n.is_completed).length} pendiente{notes.filter(n => !n.is_completed).length !== 1 ? 's' : ''}
              </span>
            </h2>
          </div>
          <div className="divide-y max-h-[400px] overflow-y-auto">
            {notes.map((note) => (
              <div
                key={note.id}
                className={`flex items-start gap-3 p-4 ${
                  note.is_completed ? 'opacity-50' : ''
                }`}
              >
                <div className="shrink-0 mt-0.5">
                  {note.is_completed ? (
                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                  ) : (
                    <Circle className="w-5 h-5 text-gray-300" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`px-2 py-0.5 rounded text-xs font-mono font-semibold ${
                      note.is_completed
                        ? 'bg-green-100 text-green-700'
                        : 'bg-amber-100 text-amber-800'
                    }`}>
                      {note.location_label}
                    </span>
                    <span className={`text-xs font-medium ${
                      note.is_completed ? 'text-green-600' : 'text-amber-600'
                    }`}>
                      {note.is_completed ? 'Listo' : 'Pendiente'}
                    </span>
                  </div>
                  <p className={`text-sm ${note.is_completed ? 'text-gray-400 line-through' : 'text-gray-700'}`}>
                    {note.note}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {new Date(note.created_at).toLocaleDateString('es-CL', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric'
                    })}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Acciones rápidas */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Link
          to="/teacher/cohorts"
          className="flex items-center gap-4 p-4 bg-card rounded-xl border hover:border-primary transition-colors"
        >
          <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
            <UsersRound className="w-6 h-6 text-orange-600" />
          </div>
          <div>
            <p className="font-medium">Mis Cohortes</p>
            <p className="text-sm text-muted-foreground">Gestionar grupos</p>
          </div>
          <ChevronRight className="w-5 h-5 text-muted-foreground ml-auto" />
        </Link>

        <Link
          to="/teacher/preview/units"
          className="flex items-center gap-4 p-4 bg-card rounded-xl border hover:border-primary transition-colors"
        >
          <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
            <Eye className="w-6 h-6 text-green-600" />
          </div>
          <div>
            <p className="font-medium">Ver el curso</p>
            <p className="text-sm text-muted-foreground">Previsualizar lecciones y ejercicios</p>
          </div>
          <ChevronRight className="w-5 h-5 text-muted-foreground ml-auto" />
        </Link>

        <Link
          to="/teacher/content"
          className="flex items-center gap-4 p-4 bg-card rounded-xl border hover:border-primary transition-colors"
        >
          <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
            <FolderOpen className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <p className="font-medium">Contenido</p>
            <p className="text-sm text-muted-foreground">Ver cursos</p>
          </div>
          <ChevronRight className="w-5 h-5 text-muted-foreground ml-auto" />
        </Link>
      </div>
    </div>
  )
}
